import { useMemo } from 'react';
import classnames from 'classnames';
import { MdOutlineInfo } from 'react-icons/md';
import { AircraftIcon, CarGroundIcon } from '@icons';
import { Tooltip } from '@ds';
import { TransportType } from '@lp/features/RoutePlanner/components/TransportType';
import { LogisticsMetricsItem } from '@lp/features/RoutePlanner/utils';
import { RouteLegTypeEnum, RouteTypeEnum } from '@/api/apis/route';
import { AVIA_LEG_TYPES, GROUND_LEG_TYPES } from '@/utils/route';
import { msToFixedHours } from '@/utils/helpers';

const COLORS_BY_TYPE: Record<RouteTypeEnum, Record<string, string>> = {
  [RouteTypeEnum.Main]: {
    bg: 'bg-base-70',
    title: 'text-base-5',
    list: 'text-base-10',
    listIcons: 'text-base-5',
    listIconsBg: 'bg-base-5',
  },
  [RouteTypeEnum.DryRun]: {
    bg: 'bg-transparent',
    title: 'text-base-10',
    list: 'text-base-20',
    listIcons: 'text-base-20',
    listIconsBg: 'bg-base-20',
  },
};

const TEXTS_BY_TYPE: Record<RouteTypeEnum, Record<string, string>> = {
  [RouteTypeEnum.Main]: {
    title: 'Main',
    aviaTooltipText: 'Total flight hours summed up for all air providers, except for Commercial Flight.',
    groundTooltipText: 'Driving hours summed up for all providers, except for TEDS.',
  },
  [RouteTypeEnum.DryRun]: {
    title: 'Dry Run',
    aviaTooltipText:
      'Total flying of all air providers, except for Commercial Flight, and/or driving hours, except for TEDS, from Hub-DH + DH-Hub segments, excluding surgeon’s legs, despite the Dry Run route has only DH-Hub segment.',
    groundTooltipText:
      'Total flying of all air providers, except for Commercial Flight, and/or driving hours, except for TEDS, from Hub-DH + DH-Hub segments, excluding surgeon’s legs, despite the Dry Run route has only DH-Hub segment.',
  },
};

type TimeMetricsItemProps = {
  type: RouteTypeEnum;
  metricsList: LogisticsMetricsItem[];
};

export const TimeMetricsItem = ({ type, metricsList }: TimeMetricsItemProps) => {
  const colors = COLORS_BY_TYPE[type];
  const texts = TEXTS_BY_TYPE[type];

  const titleMetrics = useMemo(() => {
    if (!metricsList.length) return null;

    const hasAviaLegs = metricsList.some((m) => AVIA_LEG_TYPES.includes(m.provider));
    const list = metricsList.filter((m) => !hasAviaLegs || AVIA_LEG_TYPES.includes(m.provider));

    return {
      isGround: !hasAviaLegs,
      providers: hasAviaLegs ? list.map((m) => m.provider) : [RouteLegTypeEnum.TmdxDriveAffiliateGroundLeg],
      actualTimeMs: list.reduce((acc, m) => acc + m.actualTimeMs, 0),
    };
  }, [metricsList]);

  const tooltipText = titleMetrics?.isGround ? texts.groundTooltipText : texts.aviaTooltipText;

  return (
    <div className={classnames('px-3 py-2 flex grow shrink-0 flex-col gap-4 rounded-lg', colors.bg)}>
      <div className={classnames('flex gap-2', colors.title)}>
        <div className="flex gap-1 items-center">
          <span className="text-l-sb">{texts.title}</span>
          <Tooltip content={tooltipText} classNames={{ base: 'max-w-120' }}>
            <div>
              <MdOutlineInfo size={16} />
            </div>
          </Tooltip>
        </div>
        {!titleMetrics && <span className="text-l-sb">N/A</span>}
        {titleMetrics && (
          <>
            {titleMetrics.providers.map((p) => {
              const icon = titleMetrics.isGround ? <CarGroundIcon /> : <AircraftIcon />;

              return (
                <div key={p} className="flex items-center gap-1">
                  {!titleMetrics.isGround && <TransportType legType={p} />}
                  {icon}
                </div>
              );
            })}
            <span className="text-l-sb">{msToFixedHours(titleMetrics.actualTimeMs)}</span>
          </>
        )}
      </div>
      <div className="flex flex-col gap-1">
        {!metricsList.length && <span className="text-m-r text-base-20">There is no route yet</span>}
        {!!metricsList.length &&
          metricsList.map((m) => {
            const isGround = GROUND_LEG_TYPES.includes(m.provider);
            const icon = isGround ? <CarGroundIcon /> : <AircraftIcon />;
            const fullTimeString = msToFixedHours(m.actualTimeMs);
            const blockTimeString = m.blockTimeMs && msToFixedHours(m.blockTimeMs);

            return (
              <div key={m.provider} className="flex gap-2">
                <div className={classnames('flex items-center gap-1', colors.listIcons)}>
                  <TransportType legType={m.provider} bgColor={colors.listIconsBg} />
                  {icon}
                </div>
                <span className={classnames('text-m-m', colors.list)}>
                  {blockTimeString ? `${fullTimeString} / ${blockTimeString}` : fullTimeString}
                </span>
              </div>
            );
          })}
      </div>
    </div>
  );
};
